import React, { useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import QueryCard from 'components/Meteorology/Shared/QueryCard';
import QueryCardHeader from 'components/Meteorology/Shared/QueryCard/Header';
import QueryCardChips from 'components/Meteorology/Shared/QueryCard/Chips';
import QueryCardChip from 'components/Meteorology/Shared/QueryCard/Chip';
import QueryCardTrigger from 'components/Meteorology/Shared/QueryCard/Trigger';
import Snackbar from 'components/DesignSystem/Snackbar';
import UnicenarioTable from 'components/Flux/Unicenario/Table';
import RevisionSubTableModal from 'components/Flux/Revision/SubTableModal';
import useFluxAutomaticZipDownload from 'utils/hooks/flux/automatic/useFluxAutomaticZipDownload';
import useUnicenarioData from 'utils/hooks/flux/automatic/useUnicenarioData';
import useUnicenarioScenarios from 'utils/hooks/flux/automatic/useUnicenarioScenarios';
import usePeriodControls from 'hooks/Flux/Automatic/Revision/usePeriodControls';
import {
  selectFluxAutomaticControlPanelMltOption,
  selectFluxAutomaticControlPanelSelectedRevision,
  selectFluxAutomaticControlPanelUnicenarioScenario,
} from 'redux/reducers/fluxAutomaticControlPanelReducer';

import ControlPanelSheet from './ControlPanelSheet';

import './index.scss';

export default function UnicenarioMobile() {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [period, setPeriod] = useState('');

  const unidade = useSelector(selectFluxAutomaticControlPanelMltOption);
  const unicenarioScenario = useSelector(selectFluxAutomaticControlPanelUnicenarioScenario);
  const review = useSelector(selectFluxAutomaticControlPanelSelectedRevision);

  const {
    isFetching, hasData, unicenarioSections, colLabels,
  } = useUnicenarioData();

  const {
    data,
    isFetching: isFetchingScenarios,
    errorMessage,
  } = useUnicenarioScenarios(period);

  const {
    toFirst, toPrevious, toNext, toLast, isFirstPeriod, isLastPeriod,
  } = usePeriodControls(period, colLabels, setPeriod);

  const { DownloadZipButton, DownloadConfirmationDialog } = useFluxAutomaticZipDownload({
    models: unicenarioScenario ? [unicenarioScenario] : [],
    review,
    unidade,
    type: 'unicenario',
    pastSimulationNames: [],
    spatialSubdivisionParam: {},
  });

  const popDataModal = (chosenPeriod: string) => {
    setIsOpen(true);
    setPeriod(chosenPeriod);
  };

  const chips = useMemo(() => {
    const result: string[] = [];
    if (review) result.push(`Revisão: ${review}`);
    if (unidade) result.push(`Unidade: ${unidade}`);
    return result;
  }, [review, unidade]);

  return (
    <div className="flux-automatic-unicenario-mobile">
      <DownloadConfirmationDialog />
      <RevisionSubTableModal
        isOpen={isOpen}
        period={period}
        data={data?.data}
        revision={review}
        modelName={unicenarioScenario ?? ''}
        errorMessage={errorMessage}
        onClose={() => setIsOpen(false)}
        toFirst={toFirst}
        toPrevious={toPrevious}
        toNext={toNext}
        toLast={toLast}
        isFirstPeriod={isFirstPeriod}
        isLastPeriod={isLastPeriod}
        isFetchingScenarios={isFetchingScenarios}
      />

      <QueryCard>
        <QueryCardHeader
          label="Consulta atual"
          title={unicenarioScenario || 'Nenhum cenário selecionado'}
          action={unicenarioScenario ? <DownloadZipButton /> : undefined}
        >
          {chips.length > 0 && (
            <QueryCardChips>
              {chips.map((chip) => <QueryCardChip key={chip}>{chip}</QueryCardChip>)}
            </QueryCardChips>
          )}
        </QueryCardHeader>
        <QueryCardTrigger onClick={() => setSheetOpen(true)} />
      </QueryCard>

      {isFetching && <div className="text-center py-4"><Spinner /></div>}

      {!isFetching && !unicenarioScenario && (
        <Snackbar>
          Selecione um cenário no painel de controle para visualizar os dados.
        </Snackbar>
      )}

      {!isFetching && unicenarioScenario && !hasData && (
        <Snackbar>
          Nenhum dado para os filtros selecionados.
        </Snackbar>
      )}

      {!isFetching && unicenarioScenario && hasData && (
        <div className="flux-table-card">
          <div className="table-responsive flux-table-card-body flux-automatic-unicenario-mobile-table">
            <UnicenarioTable
              unicenarioSections={unicenarioSections}
              colLabels={colLabels}
              popDataModal={popDataModal}
              stickyRowLabel
            />
          </div>
        </div>
      )}

      <ControlPanelSheet open={sheetOpen} dismiss={() => setSheetOpen(false)} />
    </div>
  );
}
